/**
 * Service utilities for handling base_url (params) configuration
 */

export interface ServiceBaseUrl {
  [key: string]: string;
}

/** 編集UIで行の順序を保持するためのメタキー（永続化時は除去） */
export const BASE_URL_ROW_ORDER_META = "__meta_row_order__";

/** 環境名が未入力の行を保持するための内部キー接頭辞（永続化時は除去） */
export const BASE_URL_DRAFT_PREFIX = "__draft_empty_";

function isInternalBaseUrlKey(k: string): boolean {
  return k === BASE_URL_ROW_ORDER_META || k.startsWith(BASE_URL_DRAFT_PREFIX);
}

/**
 * ServiceDetails から base_url 設定を取得
 *
 * @param params ServiceDetailsオブジェクト
 * @returns base_url 配列（1ブロック目が編集対象）
 */
export function getServiceBaseUrls(params: any): ServiceBaseUrl[] {
  if (!params || typeof params !== "object") {
    return [];
  }

  const list = Array.isArray(params.base_url)
    ? params.base_url
    : Array.isArray(params.baseUrl)
      ? params.baseUrl
      : [];
  return list.filter((d: any) => d && typeof d === "object");
}

/**
 * params から先頭の base_url ブロックを取得（なければ空オブジェクト）
 */
export function getFirstServiceBaseUrlBlockOrEmpty(params: any): ServiceBaseUrl {
  const blocks = getServiceBaseUrls(params);
  return blocks.length > 0 ? blocks[0] : {};
}

/**
 * ServiceDetails に base_url 設定を設定する
 *
 * @param params ServiceDetailsオブジェクト
 * @param baseUrls 設定する base_url 配列
 * @returns 更新されたparamsオブジェクト
 */
export function setServiceBaseUrls(params: any, baseUrls: ServiceBaseUrl[]): any {
  if (!params || typeof params !== "object") {
    return { base_url: baseUrls };
  }

  const updated = { ...params };
  delete updated.baseUrl;
  updated.base_url = baseUrls;

  return updated;
}

/**
 * 1ブロックのオブジェクトを行配列に変換（編集用）
 */
export function serviceBlockToRows(block: ServiceBaseUrl): [string, string][] {
  const orderStr = block[BASE_URL_ROW_ORDER_META];
  if (typeof orderStr === "string" && orderStr.length > 0) {
    const keys = orderStr.split(",");
    return keys.map((k) => {
      if (k.startsWith(BASE_URL_DRAFT_PREFIX)) {
        return ["", block[k] ?? ""] as [string, string];
      }
      return [k, block[k] ?? ""] as [string, string];
    });
  }
  const entries = Object.entries(block).filter(([k]) => !isInternalBaseUrlKey(k));
  if (entries.length === 0) {
    return [["", ""]];
  }
  return entries.map(([k, v]) => [k, v] as [string, string]);
}

/**
 * 行配列を1ブロックのオブジェクトに変換（内部メタ・ドラフトキー付き）
 */
export function rowsToServiceBlock(rows: [string, string][]): ServiceBaseUrl {
  const o: ServiceBaseUrl = {};
  const order: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const [k, v] = rows[i];
    const trimmed = k.trim();
    if (trimmed) {
      o[trimmed] = v;
      order.push(trimmed);
    } else {
      const dk = `${BASE_URL_DRAFT_PREFIX}${i}`;
      o[dk] = v;
      order.push(dk);
    }
  }
  o[BASE_URL_ROW_ORDER_META] = order.join(",");
  return o;
}

/**
 * 永続化用に内部キーと空の環境名を除去した1ブロック
 */
export function sanitizeBaseUrlBlockForPersistence(
  block: ServiceBaseUrl
): ServiceBaseUrl {
  const out: ServiceBaseUrl = {};
  for (const [k, v] of Object.entries(block)) {
    if (isInternalBaseUrlKey(k)) {
      continue;
    }
    if (k.trim() === "") {
      continue;
    }
    out[k] = v;
  }
  return out;
}

/**
 * 編集行から保存用の base_url 配列（単一ブロック）を生成
 */
export function persistServiceParamsBaseUrl(
  rows: [string, string][]
): ServiceBaseUrl[] {
  const merged = rowsToServiceBlock(rows);
  const clean = sanitizeBaseUrlBlockForPersistence(merged);
  return Object.keys(clean).length > 0 ? [clean] : [];
}

/**
 * 特定の環境のbaseURLを取得
 *
 * @param params ServiceDetailsオブジェクト
 * @param environment 環境名（dev, stg, prod等）
 * @param fallbackEnvironment フォールバック環境名（デフォルト: "default"）
 * @returns 該当環境のURL、見つからない場合はundefined
 */
export function getServiceBaseUrl(
  params: any,
  environment: string,
  fallbackEnvironment: string = "default"
): string | undefined {
  const baseUrls = getServiceBaseUrls(params);

  for (const baseUrl of baseUrls) {
    const clean = sanitizeBaseUrlBlockForPersistence(baseUrl);
    if (clean[environment]) {
      return clean[environment];
    }
  }

  if (environment !== fallbackEnvironment) {
    for (const baseUrl of baseUrls) {
      const clean = sanitizeBaseUrlBlockForPersistence(baseUrl);
      if (clean[fallbackEnvironment]) {
        return clean[fallbackEnvironment];
      }
    }
  }

  return undefined;
}
